import { OnboardingStepType } from "@yardzen/api-dtos";

export interface OnboardingStepUIDetails {
  displayName: string;
  iconName: string;
  path: string;
  stage: number;
  description: string;
  imageSrc: string;
  dialog: {
    title: string;
    description: string;
    icon: string;
  };
}

// Define a type that can be either an OnboardingStepType or one of the additional step IDs
export type StepId =
  | OnboardingStepType
  | "review-design"
  | "feedback"
  | "revisions"
  | "materials"
  | "contractor"
  | "installation";

export const ONBOARDING_STEP_UI_DETAILS: Record<
  StepId,
  OnboardingStepUIDetails
> = {
  // Stage 1 Steps
  [OnboardingStepType.GOALS]: {
    displayName: "My Goals",
    iconName: "flag",
    path: "/onboarding/goals",
    stage: 1,
    description:
      "Tell us about your dream outdoor space. What are your main goals for this project? How do you envision using your new yard?",
    imageSrc: "/images/goals-sample.jpg",
    dialog: {
      title: "Let's Talk Goals",
      description:
        "Share what you hope to achieve with your yard—whether it's a space to relax, play, grow food, or entertain. Your goals help guide every design decision.",
      icon: "challenge-yard",
    },
  },
  [OnboardingStepType.INSPIRATION]: {
    displayName: "My Inspiration",
    iconName: "lightbulb",
    path: "/onboarding/inspiration",
    stage: 1,
    description:
      "Share your style preferences and inspiration. What looks inspire you? What elements catch your eye in outdoor spaces?",
    imageSrc: "/images/inspiration-sample.jpg",
    dialog: {
      title: "Let's Talk Style",
      description:
        "Tell us what you love. Your style preferences help us create a yard that feels like you—every plant, path, and patio included.",
      icon: "plan",
    },
  },
  [OnboardingStepType.PLANTS]: {
    displayName: "My Plants",
    iconName: "seedling",
    path: "/onboarding/onboarding-plants",
    stage: 1,
    description:
      "No matter your vibe, every thriving garden starts with intention. We want to know: What matters most to you when it comes to your plants?",
    imageSrc: "/images/plants-sample.jpg",
    dialog: {
      title: "Let's talk plants",
      description:
        "One last quiz! Again, please stick with it all the way to the end!",
      icon: "planting",
    },
  },
  [OnboardingStepType.WISHLIST]: {
    displayName: "Wishlist & Budget",
    iconName: "heart",
    path: "/onboarding/budget",
    stage: 1,
    description:
      "List the key elements you want—materials, furniture, or features—then estimate costs to align with your budget.",
    imageSrc: "/images/budget-sample.jpg",
    dialog: {
      title: "Let's Talk Must-Haves",
      description:
        "Tell us what features you'd love to include and how they fit within your budget. We'll use this to guide your design and set realistic expectations.",
      icon: "one-round-of-revisions",
    },
  },
  [OnboardingStepType.PHOTOS]: {
    displayName: "Photos & Drawings",
    iconName: "image",
    path: "/onboarding/photos",
    stage: 1,
    description:
      "Help us understand your space better. Upload photos of your yard and any existing plans or drawings you have.",
    imageSrc: "/images/photos-sample.jpg",
    dialog: {
      title: "Let's Talk Visuals",
      description:
        "Let me know the tone you're going for — chill and friendly, premium and polished, or more quirky/personal.",
      icon: "sparkles",
    },
  },
  [OnboardingStepType.REVIEW]: {
    displayName: "Review & Submit to Designer",
    iconName: "clipboard-list",
    path: "/onboarding/review",
    stage: 1,
    description:
      "Review all your inputs and preferences before submitting to your design team. Make sure everything reflects your vision.",
    imageSrc: "/images/review-sample.jpg",
    dialog: {
      title: "Review & Submit",
      description: "Review your information and submit to your design team.",
      icon: "check",
    },
  },
  [OnboardingStepType.EXTERIOR]: {
    displayName: "Exterior Details",
    iconName: "home",
    path: "/onboarding/exterior",
    stage: 1,
    description:
      "Tell us about your home's exterior features and any specific requirements or preferences you have.",
    imageSrc: "/images/exterior-sample.jpg",
    dialog: {
      title: "Let's Talk Exterior",
      description:
        "Tell us about your home's exterior features and any specific requirements or preferences you have.",
      icon: "home",
    },
  },

  // Stage 2 Steps
  "review-design": {
    displayName: "Review Design",
    iconName: "blueprint",
    path: "/review-design",
    stage: 2,
    description:
      "Take a look at your initial design and get ready to give feedback.",
    imageSrc: "/images/review-design-sample.jpg",
    dialog: {
      title: "Review Your Design",
      description:
        "Take a look at your initial design and get ready to give feedback.",
      icon: "blueprint",
    },
  },
  feedback: {
    displayName: "Provide Feedback",
    iconName: "conversation",
    path: "/feedback",
    stage: 2,
    description: "Share your thoughts and suggestions to refine your design.",
    imageSrc: "/images/feedback-sample.jpg",
    dialog: {
      title: "Give Feedback",
      description: "Share your thoughts and suggestions to refine your design.",
      icon: "conversation",
    },
  },
  revisions: {
    displayName: "Revisions",
    iconName: "pencil",
    path: "/revisions",
    stage: 2,
    description: "Request changes and see your design evolve.",
    imageSrc: "/images/revisions-sample.jpg",
    dialog: {
      title: "Request Revisions",
      description: "Request changes and see your design evolve.",
      icon: "pencil",
    },
  },

  // Stage 3 Steps
  materials: {
    displayName: "Materials Selection",
    iconName: "pallet",
    path: "/materials",
    stage: 3,
    description: "Choose the materials and finishes for your project.",
    imageSrc: "/images/materials-sample.jpg",
    dialog: {
      title: "Select Materials",
      description: "Choose the materials and finishes for your project.",
      icon: "pallet",
    },
  },
  contractor: {
    displayName: "Find a Contractor",
    iconName: "handshake",
    path: "/contractor",
    stage: 3,
    description:
      "Get matched with trusted contractors to bring your vision to life.",
    imageSrc: "/images/contractor-sample.jpg",
    dialog: {
      title: "Find a Contractor",
      description:
        "Get matched with trusted contractors to bring your vision to life.",
      icon: "handshake",
    },
  },
  installation: {
    displayName: "Installation Support",
    iconName: "helmet",
    path: "/installation",
    stage: 3,
    description:
      "Access support and resources for a smooth installation process.",
    imageSrc: "/images/installation-sample.jpg",
    dialog: {
      title: "Installation Support",
      description:
        "Access support and resources for a smooth installation process.",
      icon: "helmet",
    },
  },
};
